import React, { useEffect, useState, useMemo } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { ToggleButtonGroup, ToggleButton, Typography } from '@mui/material';
import { DateRangePicker } from "rsuite";

const ResultComponent = ({
  current,
  historical,
  shortTerm,
  xAxis,
  id,
  name,
  histTime: time,
  unit,
  filterToggle = false
}) => {
  const [selectedTypes, setSelectedTypes] = useState(['current', 'historical', 'short_term']);
  const [minYRange, setMinYRange] = useState();
  const [maxYRange, setMaxYRange] = useState();
  const [minXRange, setMinXRange] = useState(null);
  const [maxXRange, setMaxXRange] = useState(null);

  // const dateValuePairs = useMemo(() => {
  //   if (!time) return [];
  //   return Object.entries(time).map(([key, value]) => ({
  //     displayDate: value.split('_')[1],
  //     fullDate: value,
  //     key
  //   }));
  // }, [time]);

  const categories = useMemo(() => {
    console.log("CURRENT:", current)
    if (!current?.points) return [];
    return xAxis;
  }, [current]);

  const calculateStats = (data) => {
    if (!data?.points) return [];

    return data.points.map(timePoint => ({
      date: timePoint.date,
      time: timePoint.time,
      min: timePoint.min,
      max: timePoint.max,
      mean: timePoint.mean,
      median: timePoint.median,
      point_start: data.point_start,
      // Arrays for tooltip
      dates: timePoint.dates || null,
      mins: timePoint.mins || null,
      maxs: timePoint.maxs || null,
      means: timePoint.means || null,
      medians: timePoint.medians || null
    }));
  };

  const currentStats = useMemo(() => calculateStats(current), [current]);
  const historicalStats = useMemo(() => calculateStats(historical), [historical]);
  const shortTermStats = useMemo(() => calculateStats(shortTerm), [shortTerm]);

  console.log("currentStats:", currentStats)

  const series = useMemo(() => {
    const seriesData = [];
    const addSeries = (stats, namePrefix) => {
      const statTypes = [
        { key: 'mean', arrayKey: 'means', name: 'Mean', symbol: 'circle' },
        { key: 'max', arrayKey: 'maxs', name: 'Max', symbol: 'diamond' },
        { key: 'min', arrayKey: 'mins', name: 'Min', symbol: 'triangle-down' },
        { key: 'median', arrayKey: 'medians', name: 'Median', symbol: 'square' }
      ];

      const pointStart = stats[0]?.point_start || 0;

      statTypes.forEach(({ key, arrayKey, name, symbol }) => {
        const data = stats.map(timePoint => {

          console.log("NAME:", namePrefix, "TIMEPOINT:", timePoint, "Array Key:", arrayKey, "BOTH:", timePoint[arrayKey], timePoint[key])
          // Check if the arrays are null
          const hasNullArrays = !timePoint[arrayKey] && timePoint[key] === 0;

          console.log("NULL:", hasNullArrays);

          return {
            y: timePoint[key],
            date: timePoint.date,
            time: timePoint.time,
            dates: timePoint.dates,
            detailedValues: timePoint[arrayKey],
            marker: hasNullArrays ? {
              enabled: false,
              symbol: symbol
            } : {
              enabled: true,
              symbol: symbol
            },
            enableMouseTracking: !hasNullArrays
          };
        });

        seriesData.push({
          name: `${namePrefix} ${name}`,
          data,
          pointStart: pointStart
        });
      });
    };

    selectedTypes.forEach(type => {
      if (type === 'current' && currentStats.length) addSeries(currentStats, 'Current');
      if (type === 'historical' && historicalStats.length) addSeries(historicalStats, 'Historical');
      if (type === 'short_term' && shortTermStats.length) addSeries(shortTermStats, 'Short Term');
    });

    return seriesData;
  }, [selectedTypes, currentStats, historicalStats, shortTermStats]);

  const chartOptions = {
    chart: { type: 'spline' },
    title: { text: `${name} Monitoring` },
    xAxis: {
      categories,
      tickInterval: Math.ceil(categories.length / 10), // Show fewer ticks for better readability
      labels: {
        rotation: -45,
        align: 'right',
        style: {
          fontSize: '11px'
        }
      }
    },
    yAxis: {
      title: { text: `${name} ${unit ? unit : ""}` }
    },
    plotOptions: {
      series: {
        pointStart: null,
        connectNulls: true,
        marker: {
          enabled: true
        }
      }
    },
    tooltip: {
      useHTML: true,
      formatter: function () {
        if (!this.point.dates?.length) {
          return `<b>${this.x}</b><br>${this.series.name}: ${this.y.toFixed(2)}`;
        }

        let html = `
        <table style="border-collapse: collapse;">
        <tr>
              <td style="padding: 2px 5px;"><b>${this.x}</b></td>
              <td style="padding: 2px 5px;"><b>${this.y.toFixed(2)}</b></td>
            </tr>
            </table>`;
        html += `<br><b>${this.series.name}</b><br/>`;
        html += '<table style="border-collapse: collapse;">';
        html += '<tr><th style="padding: 2px 5px;">Date</th><th style="padding: 2px 5px;">Value</th></tr>';

        this.point.dates.forEach((date, i) => {
          const value = this.point.detailedValues[i];
          if (value !== undefined) {
            html += `<tr>
              <td style="padding: 2px 5px;">${date}</td>
              <td style="padding: 2px 5px;">${value.toFixed(2)}</td>
            </tr>`;
          }
        });

        html += '</table>';
        return html;
      }
    },
    series
  };

  useEffect(() => {
    const chart = Highcharts.chart(id, chartOptions);
    return () => chart.destroy();
  }, [id, chartOptions]);

  const updateYAxis = () => {
    const min = parseFloat(minYRange);
    const max = parseFloat(maxYRange);
    if (!isNaN(min) && !isNaN(max)) {
      const chart = Highcharts.charts.find(c => c?.renderTo.id === id);
      chart?.yAxis[0].update({ min, max }, true);
    }
  };

  const updateXAxis = () => {
    if (!minXRange || !maxXRange) return;

    const minIndex = categories.findIndex(date => date === minXRange);
    const maxIndex = categories.findIndex(date => date === maxXRange);

    if (minIndex !== -1 && maxIndex !== -1) {
      const chart = Highcharts.charts.find(c => c?.renderTo.id === id);
      chart?.xAxis[0].update({ min: minIndex, max: maxIndex }, true);
    }
  };

  return (
    <div className="chart-container" style={{
      border: "1px solid",
      margin: "5em 5em 0 5em",
    }}>
      <div className="display-type-buttons">
        <ToggleButtonGroup
          className="toggle-button-group"
          value={selectedTypes}
          onChange={(_, newTypes) => setSelectedTypes(newTypes.length ? newTypes : selectedTypes)}
          aria-label="data type selection"
        >
          {['current', 'historical', 'short_term'].map(type => (
            <ToggleButton className="toggle-button" key={type} value={type}>
              {type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </div>
      <div className="x-y-axis-container">
        <div className="y-axis-config">
          <div className="y-axis-inputs">
            <input
              className="y-axis-input"
              type="number"
              placeholder="Min"
              onChange={e => setMinYRange(e.target.value)}
            />
            <input
              type="number"
              placeholder="Max"
              onChange={e => setMaxYRange(e.target.value)}
              className="y-axis-input"
            />
            <button className="y-axis-confirm" onClick={updateYAxis}>Set Y Axis Range</button>
          </div>
        </div>

        <div className="x-axis-config">
          <div className="x-axis-inputs">
            <DateRangePicker
              className="x-axis-input"
              style={{ width: "100%" }}
              format="dd/MM/yyyy"
              defaultCalendarValue={[new Date('2024-01-01'), new Date('2024-12-31')]}
              onOk={(date) => {
                if (date && date.length === 2) {
                  const formatDate = (d) => {
                    const month = (d.getMonth() + 1).toString().padStart(2, '0');
                    const day = d.getDate().toString().padStart(2, '0');
                    return `${month}-${day}`;
                  };

                  setMinXRange(formatDate(date[0]));
                  setMaxXRange(formatDate(date[1]));
                }
              }}
              onClean={() => {
                setMinXRange(null);
                setMaxXRange(null);
                Highcharts.charts.forEach(chart =>
                  chart?.xAxis[0].update({
                    min: null,
                    max: null
                  }, true)
                );
              }}
              showOneCalendar
              ranges={[]}
              caretAs={() => null}
              renderTitle={date => {
                if (!date) return '';
                const month = date.getMonth() + 1;
                const day = date.getDate();
                return `${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
              }}
              renderValue={(value) => {
                if (!value || !value.length) return 'Select date range';
                const formatDate = (d) => {
                  const month = (d.getMonth() + 1).toString().padStart(2, '0');
                  const day = d.getDate().toString().padStart(2, '0');
                  return `${month}-${day}`;
                };
                return `${formatDate(value[0])} - ${formatDate(value[1])}`;
              }}
            />
            <button onClick={updateXAxis} className="x-axis-confirm">Set Date Range</button>
          </div>
        </div>
      </div>


      <div id={id} />
    </div>
  );
};

export default ResultComponent;